<template>
	<main v-if="hotel" class="new-hotel-info">
		<section class="new-hotel-info__inner new-layout-container">
			<header class="new-hotel-info__header">
				<div class="new-hotel-info__title-row">
					<h1>{{hotel.name}}</h1>

					<div class="new-hotel-info__stars" :aria-label="`${hotel.starRating || 0} ${translation.starRating}`">
						<span v-for="star in stars" :key="star">&#9733;</span>
					</div>
				</div>

				<p v-if="hotel.address">{{hotel.address}}</p>
			</header>

			<section class="new-hotel-info__preview" :aria-label="translation.previewGalleryLabel">
				<button
					type="button"
					class="new-hotel-info__main-photo"
					@click="openGallery"
				>
					<img :src="mainImage.path" :alt="captionFor(mainImage)">
				</button>

				<div v-if="hasSidePreview" class="new-hotel-info__side-photos">
					<button
						v-for="image in sidePreviewImages"
						:key="image.key"
						type="button"
						class="new-hotel-info__thumb"
						@click="openGallery"
					>
						<img :src="image.path" :alt="captionFor(image)">
					</button>

					<button
						v-if="hasMorePhotos"
						type="button"
						class="new-hotel-info__more"
						@click="openGallery"
					>
						{{morePhotosLabel}}
					</button>
				</div>
			</section>

			<section v-if="hotelDescription" class="new-hotel-info__description">
				<div class="new-hotel-info__accent"></div>
				<h2>{{translation.description}}</h2>
				<p>{{hotelDescription}}</p>
			</section>
		</section>

		<div
			v-if="isGalleryOpen"
			class="new-hotel-info__gallery-overlay"
			@click.self="closeGallery"
		>
			<section class="new-hotel-info__gallery" role="dialog" aria-modal="true" :aria-label="galleryTitle">
				<header class="new-hotel-info__gallery-header">
					<h2>{{hotel.name}} <span>|</span> {{translation.galleryTitle}}</h2>
					<button type="button" class="new-hotel-info__gallery-close" :aria-label="translation.closeGallery" @click="closeGallery">
						<span></span>
						<span></span>
					</button>
				</header>

				<div class="new-hotel-info__gallery-grid">
					<figure
						v-for="image in galleryImages"
						:key="image.key"
						class="new-hotel-info__gallery-item"
					>
						<img :src="image.path" :alt="captionFor(image)">
						<figcaption>{{captionFor(image)}}</figcaption>
					</figure>
				</div>
			</section>
		</div>
	</main>
</template>

<script>
import { translate } from '../../assets/translations';
import EventService from '../../services/EventService';
import HotelInfoService from '../../services/HotelInfoService';

const FALLBACK_HOTEL_IMAGE = 'https://media.omnibees.com/Images/3795/Property/247128.jpg';

export default {
	props: {
		shortName: {
			type: String,
			required: true,
		},
		hotelId: {
			type: [String, Number],
			required: true,
		},
	},
	data() {
		return {
			event: null,
			hotel: null,
			isGalleryOpen: false,
		};
	},
	computed: {
		translation() {
			return translate(this.$store.state.language).hotelInfo;
		},
		hotelDescription() {
			const translation = this.hotelTranslation();
			return translation && translation.description ? translation.description : '';
		},
		images() {
			if (!this.hotel || !Array.isArray(this.hotel.images)) return [];
			return this.hotel.images
				.filter(image => image && image.path)
				.map((image, index) => Object.assign({}, image, {
					key: image.id || `${image.path}-${index}`,
				}));
		},
		galleryImages() {
			return this.images.length
				? this.images
				: [{ key: 'fallback-hotel-image', path: FALLBACK_HOTEL_IMAGE, title: this.hotel.name }];
		},
		mainImage() {
			return this.galleryImages[0];
		},
		sidePreviewImages() {
			return this.galleryImages.slice(1, 6);
		},
		hasSidePreview() {
			return this.sidePreviewImages.length || this.hasMorePhotos;
		},
		hasMorePhotos() {
			return this.galleryImages.length > 6;
		},
		morePhotosCount() {
			return Math.max(this.galleryImages.length - 6, 0);
		},
		morePhotosLabel() {
			if (typeof this.translation.morePhotos === 'function') {
				return this.translation.morePhotos(this.morePhotosCount);
			}
			return `More ${this.morePhotosCount} photos`;
		},
		galleryTitle() {
			return `${this.hotel.name} | ${this.translation.galleryTitle}`;
		},
		stars() {
			const starRating = Number(this.hotel.starRating) || 0;
			return [...Array(starRating)].map((value, index) => index + 1);
		},
	},
	watch: {
		isGalleryOpen(isOpen) {
			document.body.style.overflow = isOpen ? 'hidden' : '';
		},
	},
	mounted() {
		this.getHotelInfo();
	},
	beforeDestroy() {
		document.body.style.overflow = '';
	},
	methods: {
		async getHotelInfo() {
			try {
				const eventResponse = await (new EventService()).getByShortName(this.shortName);
				this.event = eventResponse.data;
				this.$store.commit('event', this.event);

				const response = await (new HotelInfoService(this.event.client)).getHotelInfo(this.hotelId, this.$store.state.language.symbol);
				this.hotel = response.data.hotels[0];
			} catch (error) {
				console.log('get hotel error. ', error);
			}
		},
		hotelTranslation() {
			if (!this.hotel || !this.hotel.translations || !this.hotel.translations.length) return {};
			const languageId = this.$store.state.language.id;
			return this.hotel.translations.find(translation => translation.language.id === languageId)
				|| this.hotel.translations[0]
				|| {};
		},
		captionFor(image) {
			return image && image.title ? image.title : this.translation.captionFallback;
		},
		openGallery() {
			this.isGalleryOpen = true;
		},
		closeGallery() {
			this.isGalleryOpen = false;
		},
	},
};
</script>

<style scoped lang="scss">
.new-hotel-info {
	min-height: 100vh;
	background: #f8f8f8;
	color: #4c4b4b;
}

.new-hotel-info__inner {
	padding: 105px 0 118px;
}

.new-hotel-info__header {
	margin-bottom: 60px;
	padding-left: 2%;
}

.new-hotel-info__title-row {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 16px;
}

.new-hotel-info h1 {
	margin: 0;
	color: #4c4b4b;
	font-size: 30px;
	font-weight: 600;
	line-height: 35px;
}

.new-hotel-info__stars {
	display: flex;
	align-items: center;
	gap: 1px;
	color: #ff9600;
	font-size: 24px;
	line-height: 1;
}

.new-hotel-info__header p {
	margin: 9px 0 0;
	color: #5a5a5a;
	font-size: 20px;
	font-weight: 400;
	line-height: 25px;
}

.new-hotel-info__preview {
	display: grid;
	grid-template-columns: minmax(0, 1.54fr) minmax(360px, .96fr);
	gap: 24px;
	margin: 0 auto 70px;
}

.new-hotel-info__main-photo,
.new-hotel-info__thumb,
.new-hotel-info__more {
	display: block;
	box-sizing: border-box;
	width: 100%;
	padding: 0;
	border: 0;
	border-radius: 11px;
	background: transparent;
	font-family: inherit;
	cursor: pointer;
}

.new-hotel-info__main-photo {
	aspect-ratio: 924 / 615;
	overflow: hidden;
}

.new-hotel-info__main-photo img,
.new-hotel-info__thumb img {
	display: block;
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.new-hotel-info__side-photos {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	grid-auto-rows: 1fr;
	gap: 24px;
}

.new-hotel-info__thumb,
.new-hotel-info__more {
	aspect-ratio: 281 / 187;
	overflow: hidden;
}

.new-hotel-info__more {
	display: flex;
	align-items: center;
	justify-content: center;
	background: #002c41;
	color: #f8f8f8;
	font-size: 20px;
	font-weight: 600;
	line-height: 23px;
	text-align: center;
}

.new-hotel-info__description {
	max-width: 1402px;
	margin: 0 auto;
}

.new-hotel-info__accent {
	width: 39px;
	height: 8px;
	margin-bottom: 22px;
	background: #e89127;
}

.new-hotel-info__description h2 {
	margin: 0 0 31px;
	color: #4c4b4b;
	font-size: 25px;
	font-weight: 600;
	line-height: 29px;
}

.new-hotel-info__description p {
	margin: 0;
	color: #4c4b4b;
	font-size: 18px;
	font-weight: 400;
	line-height: 35px;
}

.new-hotel-info__gallery-overlay {
	position: fixed;
	inset: 0;
	z-index: 80;
	display: flex;
	justify-content: center;
	align-items: flex-start;
	padding: 48px 24px 0;
	background: rgba(0, 0, 0, .22);
}

.new-hotel-info__gallery {
	width: 100%;
	max-width: 1675px;
	max-height: calc(100vh - 48px);
	overflow-y: auto;
	background: rgba(255, 255, 255, .94);
	box-shadow: 0 22px 80px rgba(0, 0, 0, .22);
}

.new-hotel-info__gallery-header {
	position: sticky;
	top: 0;
	z-index: 1;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 24px;
	min-height: 140px;
	padding: 0 80px 0 119px;
	background: #ffffff;
	box-shadow: 0 4px 4px rgba(0, 0, 0, .05);
}

.new-hotel-info__gallery-header h2 {
	margin: 0;
	color: #4c4b4b;
	font-size: 30px;
	font-weight: 600;
	line-height: 35px;
}

.new-hotel-info__gallery-header h2 span {
	font-weight: 400;
}

.new-hotel-info__gallery-close {
	position: relative;
	flex: 0 0 auto;
	width: 48px;
	height: 48px;
	padding: 0;
	border: 0;
	background: transparent;
	cursor: pointer;
}

.new-hotel-info__gallery-close span {
	position: absolute;
	top: 50%;
	left: 50%;
	width: 56px;
	height: 2px;
	background: #78aac3;
	transform-origin: center;
}

.new-hotel-info__gallery-close span:first-child {
	transform: translate(-50%, -50%) rotate(45deg);
}

.new-hotel-info__gallery-close span:last-child {
	transform: translate(-50%, -50%) rotate(-45deg);
}

.new-hotel-info__gallery-grid {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 24px;
	max-width: 1437px;
	margin: 55px auto 96px;
}

.new-hotel-info__gallery-item {
	position: relative;
	overflow: hidden;
	margin: 0;
	aspect-ratio: 463 / 308;
	border-radius: 25px;
	background: #d7d7d7;
}

.new-hotel-info__gallery-item img {
	display: block;
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.new-hotel-info__gallery-item figcaption {
	position: absolute;
	right: 0;
	bottom: 0;
	left: 0;
	box-sizing: border-box;
	min-height: 48px;
	padding: 9px 45px;
	border-radius: 0 0 25px 25px;
	background: rgba(35, 35, 35, .35);
	color: #f8f8f8;
	font-size: 15px;
	font-weight: 400;
	line-height: 30px;
}

@media (max-width: 1100px) {
	.new-hotel-info__preview {
		grid-template-columns: 1fr;
	}

	.new-hotel-info__side-photos {
		grid-template-columns: repeat(3, minmax(0, 1fr));
	}

	.new-hotel-info__gallery-grid {
		grid-template-columns: repeat(2, minmax(0, 1fr));
		padding: 0 36px;
	}
}

@media (max-width: 720px) {
	.new-hotel-info__inner {
		padding: 54px 0 80px;
	}

	.new-hotel-info__header {
		margin-bottom: 34px;
		padding-left: 0;
	}

	.new-hotel-info h1 {
		font-size: 24px;
		line-height: 29px;
	}

	.new-hotel-info__header p,
	.new-hotel-info__description p {
		font-size: 15px;
		line-height: 25px;
	}

	.new-hotel-info__stars {
		font-size: 20px;
	}

	.new-hotel-info__preview {
		gap: 14px;
		margin-bottom: 48px;
	}

	.new-hotel-info__side-photos {
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 14px;
	}

	.new-hotel-info__more {
		font-size: 15px;
		line-height: 18px;
	}

	.new-hotel-info__description h2 {
		font-size: 21px;
		line-height: 25px;
	}

	.new-hotel-info__gallery-overlay {
		padding: 20px 8px 0;
	}

	.new-hotel-info__gallery {
		max-height: calc(100vh - 20px);
		border-radius: 28px 28px 0 0;
	}

	.new-hotel-info__gallery-header {
		min-height: 96px;
		padding: 0 28px;
	}

	.new-hotel-info__gallery-header h2 {
		font-size: 20px;
		line-height: 25px;
	}

	.new-hotel-info__gallery-close {
		width: 38px;
		height: 38px;
	}

	.new-hotel-info__gallery-close span {
		width: 38px;
	}

	.new-hotel-info__gallery-grid {
		grid-template-columns: 1fr;
		gap: 16px;
		margin: 28px auto 56px;
		padding: 0 20px;
	}

	.new-hotel-info__gallery-item {
		border-radius: 18px;
	}

	.new-hotel-info__gallery-item figcaption {
		border-radius: 0 0 18px 18px;
		padding: 7px 22px;
	}
}
</style>



// WEBPACK FOOTER //
// src/components/hotel-reservation/HotelInfo.vue